import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import Pagination from "../../common/components/Pagination";
import { useDeleteExpense, useExpenses } from "../api/expenses";
import AddExpenseModal from "../components/AddExpenseModal";

const Expenses = () => {
  const { t } = useTranslation("expenses");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const { expenses, pagination, loading, refetch } = useExpenses(
    currentPage,
    pageSize,
  );
  const { deleteExpense } = useDeleteExpense();
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);

  const handleAddExpense = () => {
    setEditingExpense(null);
    setAddModalOpen(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setAddModalOpen(true);
  };

  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError(null);

      await deleteExpense(expenseToDelete.id);
      await refetch(); // Refresh the list
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setEditingExpense(null);
  };

  const handleExpenseCreated = async () => {
    await refetch(); // Refresh the list after create/update
    handleModalClose();
  };

  const formatValue = (expense) => {
    if (expense.type === "percentage") {
      return `${expense.value}%`;
    }
    return `$${parseFloat(expense.value).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate pagination from API response
  const totalItems = pagination.count;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner animation="border" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleAddExpense}
          >
            {t("addExpense")}
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("table.name")}</TableCell>
              <TableCell>{t("table.value")}</TableCell>
              <TableCell>{t("table.type")}</TableCell>
              <TableCell>{t("table.member")}</TableCell>
              <TableCell>{t("table.asset")}</TableCell>
              <TableCell>{t("table.created")}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="bodySm" color="text.secondary">
                    {t("table.noExpenses")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              expenses.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell>
                    <Typography variant="bodySm" fontWeight="medium">
                      {expense.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatValue(expense)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        expense.type === "percentage"
                          ? t("types.percentage")
                          : t("types.static")
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {expense.user_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {expense.asset_title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm" color="text.secondary">
                      {formatDate(expense.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditExpense(expense)}
                      sx={{ mr: 1 }}
                    >
                      <Edit size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(expense)}
                      color="error"
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setCurrentPage(1);
          }}
        />
      )}

      <AddExpenseModal
        open={addModalOpen}
        onClose={handleModalClose}
        onExpenseCreated={handleExpenseCreated}
        editingExpense={editingExpense}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("deleteDialog.message", { name: expenseToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            {t("deleteDialog.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t("deleteDialog.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
